/**
 * T-143-001 — Enterprise SSO Lead Capture Form
 *
 * Feature: F-185 / E-032 / US-143
 *
 * Captures enterprise organization details, IdP preferences (Okta, Entra ID, etc.),
 * and custom SSO architecture requirements.
 * Submits to the existing Brevo marketing contact sync endpoint (`POST api/marketing/contact`),
 * followed by calendar consultation onboarding (`T-143-002`).
 */

import React, { useState } from 'react';
import { landlordApi } from '../../services/api';
import { EnterpriseSsoLeadPayload } from './types';
import CalendarBookingSection from './CalendarBookingSection';

export interface EnterpriseSsoLeadFormProps {
  onSuccess?: (payload: EnterpriseSsoLeadPayload) => void;
  className?: string;
}

const COMMON_FREE_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
];

export const EnterpriseSsoLeadForm: React.FC<EnterpriseSsoLeadFormProps> = ({
  onSuccess,
  className = '',
}) => {
  const [workEmail, setWorkEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [idp, setIdp] = useState('Okta');
  const [seats, setSeats] = useState('25');
  const [units, setUnits] = useState('100');
  const [requirements, setRequirements] = useState<string[]>([
    'SAML 2.0 Web SSO',
    'Role-Based Access Control',
  ]);
  const [notes, setNotes] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const toggleRequirement = (reqName: string) => {
    setRequirements((prev) =>
      prev.includes(reqName) ? prev.filter((r) => r !== reqName) : [...prev, reqName]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const emailTrimmed = workEmail.trim().toLowerCase();
    if (!emailTrimmed) {
      setError('Corporate work email is required.');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setError('Please provide a valid corporate email address.');
      return;
    }

    const domain = emailTrimmed.split('@')[1];
    if (COMMON_FREE_EMAIL_DOMAINS.includes(domain)) {
      setError('Please use your corporate work email (e.g. name@company.com) for Enterprise SSO setup.');
      return;
    }

    if (!companyName.trim()) {
      setError('Organization name is required.');
      return;
    }

    const nameParts = (contactName.trim() || 'Enterprise Lead').split(' ');
    const firstName = nameParts[0] || 'Enterprise';
    const lastName = nameParts.slice(1).join(' ') || 'Admin';

    const numSeats = parseInt(seats.replace(/\D/g, ''), 10) || 10;
    const numUnits = parseInt(units.replace(/\D/g, ''), 10) || 50;

    setLoading(true);

    try {
      // Submit via existing Brevo contact sync endpoint: POST api/marketing/contact
      await landlordApi.post('api/marketing/contact', {
        email: emailTrimmed,
        firstName,
        lastName,
        phone: phone.trim() || undefined,
        units: numUnits,
        marketingConsent: true,
        plan: 'enterprise',
        listIds: [3],
        attributes: {
          PRODUCT: 'Enterprise_SSO_Lead',
          COMPANY: companyName.trim(),
          IDP: idp,
          SEATS: numSeats,
          UNITS: numUnits,
          FIRSTNAME: firstName,
          LASTNAME: lastName,
          SMS: phone.trim() || undefined,
          SSO_REQUIREMENTS: requirements.join(', '),
          NOTES: notes.trim() || undefined,
          SOURCE: 'enterprise_sso_portal',
        },
      });

      setSubmitted(true);
      if (onSuccess) {
        onSuccess({
          workEmail: emailTrimmed,
          companyName: companyName.trim(),
          contactName: contactName.trim(),
          phone: phone.trim(),
          idp,
          seats: numSeats,
          units: numUnits,
          requirements,
          notes: notes.trim(),
        });
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to submit enterprise inquiry. You may also schedule directly below.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="enterprise-sso-form"
      className={`max-w-4xl mx-auto space-y-8 ${className}`}
    >
      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="border-b border-slate-200 pb-5 mb-6">
          <div className="flex items-center gap-2 text-purple-600 text-xs font-bold uppercase tracking-wider mb-1">
            <i className="fas fa-shield-alt"></i>
            <span>Enterprise Security &amp; Compliance</span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 tracking-tight">
            Enterprise SSO &amp; Multi-City Governance
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Configure centralized SAML 2.0 / OIDC authentication (Okta, Entra ID, PingIdentity)
            for your property management team and portfolio operations.
          </p>
        </div>

        {submitted ? (
          <div
            data-testid="sso-success-message"
            className="p-6 bg-purple-50 border border-purple-200 rounded-xl space-y-4"
          >
            <div className="flex items-center gap-3 text-purple-900">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xl font-bold">
                ✓
              </div>
              <div>
                <h3 className="font-bold text-lg">Enterprise Request Confirmed</h3>
                <p className="text-sm text-purple-700">
                  We’ve recorded your SSO architecture inquiry for <strong>{companyName}</strong>. Our
                  security engineering team has dispatched your SAML SP metadata packet to{' '}
                  <strong>{workEmail}</strong>.
                </p>
              </div>
            </div>

            <div className="pt-2">
              <CalendarBookingSection
                tier="enterprise"
                campaign="enterprise-sso-success"
                title="Lock In Your Architecture Review"
                description="Pick a time on our solutions architect's calendar to review IdP assertion mappings, SCIM automated user provisioning, and staging validation."
                ctaText="Select Consultation Slot"
              />
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div
                data-testid="sso-error-message"
                className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-sm flex items-start gap-3"
              >
                <i className="fas fa-exclamation-circle text-rose-500 mt-0.5 text-base"></i>
                <div className="flex-1 font-medium">{error}</div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Corporate Work Email <span className="text-rose-500">*</span>
                </label>
                <input
                  data-testid="sso-work-email"
                  type="email"
                  required
                  placeholder="alex@brokerage.com"
                  value={workEmail}
                  onChange={(e) => setWorkEmail(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Organization / Agency Name <span className="text-rose-500">*</span>
                </label>
                <input
                  data-testid="sso-company-name"
                  type="text"
                  required
                  placeholder="Lone Star Property Partners"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Primary Identity Provider
                </label>
                <select
                  data-testid="sso-idp-select"
                  value={idp}
                  onChange={(e) => setIdp(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="Okta">Okta</option>
                  <option value="Microsoft Entra ID">Microsoft Entra ID (Azure AD)</option>
                  <option value="Google Workspace">Google Workspace SAML</option>
                  <option value="PingIdentity">PingIdentity</option>
                  <option value="OneLogin">OneLogin</option>
                  <option value="Custom SAML 2.0">Custom SAML 2.0 Provider</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Estimated Team Seats
                </label>
                <input
                  data-testid="sso-seats-input"
                  type="number"
                  min="5"
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Managed Portfolio Doors
                </label>
                <input
                  data-testid="sso-units-input"
                  type="number"
                  min="1"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Technical Lead Name
                </label>
                <input
                  data-testid="sso-contact-name"
                  type="text"
                  placeholder="Alex Mercer"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                  Direct Phone
                </label>
                <input
                  data-testid="sso-phone-input"
                  type="tel"
                  placeholder="(512) 555-0188"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                Enterprise Requirements
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
                {[
                  'SAML 2.0 Web SSO',
                  'SCIM 2.0 User Provisioning',
                  'Role-Based Access Control',
                  'Custom IdP Attribute Mapping',
                  'Audit Log Streaming / Syslog',
                  'Multi-City Texas Jurisdiction Controls',
                ].map((req) => (
                  <label
                    key={req}
                    className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={requirements.includes(req)}
                      onChange={() => toggleRequirement(req)}
                      className="rounded text-purple-600 focus:ring-purple-500"
                    />
                    <span>{req}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1">
                Additional Architecture Notes
              </label>
              <textarea
                data-testid="sso-notes-input"
                rows={2}
                placeholder="Mention any custom domains, IdP federations, or specific deployment timelines..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3.5 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="pt-2">
              <button
                data-testid="sso-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-6 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-400 text-white font-bold text-base rounded-xl shadow-lg shadow-purple-900/20 transition flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    <span>Connecting with Enterprise Gateway...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-key"></i>
                    <span>Request Enterprise SSO Configuration</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Embedded Calendar Booking section */}
      {!submitted && (
        <CalendarBookingSection
          tier="enterprise"
          campaign="enterprise-sso-bottom"
          title="Prefer to Speak Directly with an Architect?"
          description="Skip the form and pick a 20-minute slot on our engineering calendar. We’ll review your IdP requirements, metadata exchange, and staging rollout live."
          ctaText="Open Architecture Calendar"
        />
      )}
    </div>
  );
};

export default EnterpriseSsoLeadForm;
